import {createSelector} from '@reduxjs/toolkit'
import {
  getCurrencyCode,
  getLocaleCode,
  getUserMeasurementSystem
} from 'modules/meta/selectors'
import {getHotelId, getPlaceId} from 'modules/search/selectors'
import {extractFilterStringFromTaxonomy} from 'utils/server/searchSeo/getFiltersFromTaxonomy'

import {seoApi} from '.'

import type {HotelAverageRateParams} from 'pages/api/seo/hotels/average-rate'
import type {HotelDescriptionParams} from 'pages/api/seo/hotels/description'
import type {HotelMetadataParams} from 'pages/api/seo/hotels/metadata'
import type {HotelPriceInsightsFAQParams} from 'pages/api/seo/hotels/price-insights-faqs'
import type {PopularLinksParams} from 'pages/api/seo/interlinking/popular'
import type {RelatedLinksParams} from 'pages/api/seo/interlinking/related'
import type {TopHotelsParams} from 'pages/api/seo/interlinking/top-hotels'
import type {BreadcrumbParams} from 'pages/api/seo/links/breadcrumb'
import type {RootState} from 'store'

export const getPopularLinksParams = createSelector(
  [getLocaleCode, getPlaceId],
  (locale, placeId): PopularLinksParams => {
    return placeId ? {locale, placeId} : {locale}
  }
)

const getPlaceIdParam = (_, placeId: string | string[] | undefined) => placeId

export const getBreadcrumbParams = createSelector(
  [getLocaleCode, getPlaceIdParam],
  (locale, placeId): BreadcrumbParams => ({placeId, locale})
)

/**
 * Fetched for the country tab of the top hotels blocks, which drops the hotels
 * already listed in the city tab and still needs enough left to fill its list.
 */
export const COUNTRY_TOP_HOTELS_LIMIT = 20

const getLimitParam = (_, __, limit?: number) => limit

export const getTopHotelsParams = createSelector(
  [getLocaleCode, getPlaceIdParam, getLimitParam],
  (locale, placeId, limit): TopHotelsParams => ({
    locale,
    ...(placeId && {placeId}),
    ...(limit && {limit})
  })
)

const getTaxonomyParam = (_, taxonomy: string | string[] | undefined) =>
  taxonomy

const getStateParam = (state: RootState) => state

export const getSeoLinkParams = createSelector(
  [getTaxonomyParam, getLocaleCode, getPlaceId],
  (taxonomy, locale, placeId) => {
    const attributes = extractFilterStringFromTaxonomy(taxonomy)
    const params: RelatedLinksParams = {locale, placeId, attributes}
    return params
  }
)

export const getHotelMetadataParams = createSelector(
  [getHotelId, getLocaleCode],
  (hotelId, locale): HotelMetadataParams => ({hotelId, locale})
)

const getHotelIdParam = (_, hotelId: string | number) => hotelId

export const getHotelPriceInsightsFAQParams = createSelector(
  [getHotelIdParam, getLocaleCode, getCurrencyCode],
  (hotelId, locale, currency): HotelPriceInsightsFAQParams => ({
    hotelId,
    locale,
    currency
  })
)

export const getHotelAverageRateParams = createSelector(
  [getHotelIdParam, getCurrencyCode],
  (hotelId, currency): HotelAverageRateParams => ({hotelId, currency})
)

export const getHotelDescriptionParams = createSelector(
  [getHotelIdParam, getLocaleCode, getUserMeasurementSystem],
  (hotelId, locale, measurementSystem): HotelDescriptionParams => ({
    hotelId,
    locale,
    measurementSystem
  })
)

const getLinksMetadataData = createSelector(
  [getStateParam, getSeoLinkParams],
  (state, params) =>
    seoApi.endpoints.getLinksMetadata.select(params)(state)?.data
)

const getLinksFAQData = createSelector(
  [getStateParam, getSeoLinkParams],
  (state, params) => seoApi.endpoints.getLinksFAQ.select(params)(state)?.data
)

export const getIsSeoContentReady = createSelector(
  [getLinksMetadataData, getLinksFAQData],
  (metadata, faq) => metadata?.default === false && faq?.faqs?.length > 0
)

export const getSeoBreadcrumbData = createSelector(
  [getStateParam, getBreadcrumbParams],
  (state, params) =>
    seoApi.endpoints.getBreadcrumb.select(params)(state)?.data?.breadcrumb
)

export const getHotelDescriptionData = createSelector(
  [getHotelIdParam, getStateParam, getHotelDescriptionParams],
  (_, state, params) =>
    seoApi.endpoints.getHotelDescription.select(params)(state)?.data
      ?.description
)

export const getHotelAverageRates = createSelector(
  [getHotelIdParam, getStateParam, getHotelAverageRateParams],
  (_, state, params) =>
    seoApi.endpoints.getHotelAverageRate.select(params)(state)?.data
)
